import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MediaItem } from '@icetus/contracts';
import { Badge, Button, formatBytes } from '@icetus/ui';
import { fetchMedia } from '../api';
import { ImageUploader } from './image-uploader';
import { Modal } from './overlay';

/**
 * 从媒体库挑图。
 *
 * 为什么值得单独做一个弹窗，而不是在每个输入框旁边加一个下拉：
 * 客户要的判断是「这张图对不对」，那必须看见缩略图；而媒体库的图会越攒越多，
 * 所以搜索与翻页是同一个能力的两半 —— 只有网格没有搜索，传到第 80 张之后
 * 这个弹窗就退化成"一个更长的列表"，等于没做。
 *
 * 三个刻意的取舍：
 *
 * 1. **弹窗里也能上传**。客户的真实动作是"我记得传过类似的一张"→ 找不到
 *    →"那我再传一张"。把上传挡在弹窗外，他就得关掉弹窗、切到媒体库、
 *    传完再回来点一次 —— 这条路径没人愿意走第二遍。
 * 2. **选中状态按 id 记住，跨页保留**。图集要从第 1 页挑两张、第 3 页挑一张，
 *    翻页就丢选择会让人以为自己点错了。
 * 3. **缩略图用派生图**。列表里 24 张原图（每张可能 3MB）会让弹窗打开就卡；
 *    `card` 档（640px）在 3~4 列的格子里已经够看，派生图没生成好时才回落到原图。
 */

const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 300;

/** 用派生图代替原图；派生图还没生成好（pending/failed）时回落到原图 */
export function mediaThumbUrl(item: MediaItem): string {
  const card = item.variants['card-webp'] ?? item.variants['card-avif'];
  if (!card) return item.url;
  // url 一定以 objectKey 结尾（由 publicBase + key 拼成），替换尾部即得派生图地址
  return item.url.endsWith(item.objectKey)
    ? `${item.url.slice(0, -item.objectKey.length)}${card.key}`
    : item.url;
}

export function MediaPickerDialog({
  open,
  tenantId,
  siteId,
  multiple = false,
  title,
  onClose,
  onConfirm,
}: {
  open: boolean;
  tenantId: string;
  siteId: string;
  multiple?: boolean;
  title?: string;
  onClose: () => void;
  /** 单选时数组长度为 1 */
  onConfirm: (items: MediaItem[]) => void;
}) {
  const [items, setItems] = useState<MediaItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [query, setQuery] = useState('');
  const [phase, setPhase] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Map<string, MediaItem>>(new Map());
  const requestId = useRef(0);

  /**
   * 搜索防抖。
   *
   * 每敲一个字就发一次请求，会在"输入 8 个字符"时打出 8 次查询，而前 7 次
   * 的结果都会被立刻覆盖。300ms 是"停手就查"的经验值：比人敲字间隔长，
   * 又短到不会让人觉得卡。
   */
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(keyword.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [keyword]);

  const load = useCallback(
    async (targetPage: number, q: string) => {
      const seq = ++requestId.current;
      setPhase((prev) => (prev === 'ready' ? prev : 'loading'));
      try {
        const data = await fetchMedia(tenantId, siteId, targetPage, PAGE_SIZE, q);
        // 慢请求后到：只认最后一次，否则搜索"a"的旧结果会盖掉"abc"的新结果
        if (seq !== requestId.current) return;
        setItems(data.items);
        setTotal(data.total);
        setError(null);
        setPhase('ready');
      } catch (err) {
        if (seq !== requestId.current) return;
        setError((err as Error).message);
        setPhase('error');
      }
    },
    [tenantId, siteId],
  );

  // 每次打开都从"未选、第一页、无关键字"开始：关掉再打开时还留着上次的选择，
  // 客户点「确定」就会拿到一张他这轮根本没点的图
  useEffect(() => {
    if (!open) return;
    setSelected(new Map());
    setKeyword('');
    setQuery('');
    setPage(1);
  }, [open]);

  useEffect(() => {
    if (!open || !siteId) return;
    void load(page, query);
  }, [open, siteId, page, query, load]);

  const toggle = (item: MediaItem) => {
    if (!multiple) {
      onConfirm([item]);
      return;
    }
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.set(item.id, item);
      return next;
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const chosen = useMemo(() => [...selected.values()], [selected]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={title ?? (multiple ? '从媒体库选择图片' : '从媒体库选择一张图')}
      description="图片按站点分开：这里只列出当前站点上传过的素材。"
      footer={
        <>
          <span className="muted">
            {multiple ? `已选 ${chosen.length} 张` : '点一张图即可选用'}
          </span>
          <div className="picker-foot__actions">
            <Button variant="ghost" onClick={onClose}>
              取消
            </Button>
            {multiple ? (
              <Button
                disabled={chosen.length === 0}
                onClick={() => onConfirm(chosen)}
              >
                {chosen.length > 0 ? `使用这 ${chosen.length} 张` : '使用所选'}
              </Button>
            ) : null}
          </div>
        </>
      }
    >
      <div className="picker-bar">
        <input
          className="field"
          value={keyword}
          placeholder="搜索文件名…"
          aria-label="搜索媒体库"
          onChange={(e) => setKeyword(e.target.value)}
        />
        <ImageUploader
          tenantId={tenantId}
          siteId={siteId}
          label="上传新图"
          onUploaded={async (uploaded) => {
            // 传完直接选中：客户上传的目的就是"用这张"，再让他去网格里点一次
            // 只会怀疑"传上去了吗"
            if (multiple) {
              setSelected((prev) => {
                const next = new Map(prev);
                for (const item of uploaded) next.set(item.id, item);
                return next;
              });
            }
            setPage(1);
            await load(1, query);
            if (!multiple && uploaded[0]) onConfirm([uploaded[0]]);
          }}
        />
        <span className="muted picker-bar__count">
          {phase === 'loading' ? '加载中…' : phase === 'error' ? '加载失败' : `共 ${total} 张`}
        </span>
      </div>

      {error ? <p className="error">{error}</p> : null}

      {phase === 'loading' ? (
        <p className="muted picker-hint">正在读取媒体库…</p>
      ) : items.length === 0 ? (
        <p className="muted picker-hint">
          {query
            ? `没有文件名含「${query}」的图片。清空搜索看看全部素材。`
            : '这个站点还没有上传过图片。点上面的「上传新图」开始。'}
        </p>
      ) : (
        <div className="picker-grid">
          {items.map((item) => {
            const on = selected.has(item.id);
            return (
              <button
                key={item.id}
                type="button"
                className={`picker-cell${on ? ' picker-cell--on' : ''}`}
                aria-pressed={multiple ? on : undefined}
                onClick={() => toggle(item)}
                title={item.originalName ?? item.objectKey}
              >
                <img src={mediaThumbUrl(item)} alt={item.alt ?? ''} loading="lazy" />
                <span className="picker-cell__meta">
                  {item.width && item.height ? `${item.width}×${item.height}` : '—'} ·{' '}
                  {formatBytes(item.bytes)}
                </span>
                {on ? <span className="picker-cell__check" aria-hidden="true">✓</span> : null}
                {/* 使用次数只做提示：客户在选图时通常不关心，但"这张已经在 3 处用着"
                    能解释他为什么觉得眼熟 */}
                {item.usageCount && item.usageCount > 0 ? (
                  <span className="picker-cell__badge">
                    <Badge tone="neutral">已用 {item.usageCount} 处</Badge>
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}

      {totalPages > 1 && phase !== 'error' ? (
        <div className="toolbar toolbar--end">
          <Button variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            上一页
          </Button>
          <span className="muted">
            {page} / {totalPages}
          </span>
          <Button
            variant="ghost"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            下一页
          </Button>
        </div>
      ) : null}
    </Modal>
  );
}

/**
 * 「从媒体库选择」按钮 + 弹窗。
 *
 * 给已经有自己一套预览的落点用（站点品牌里 Logo 是压在**真实页头配色**上
 * 预览的，商品图集要的是缩略图列表 + 排序）。这些地方不该被 ImageField
 * 统一成"输入框 + 小方图"——它们要表达的东西比一个地址多。
 */
export function MediaPickerButton({
  tenantId,
  siteId,
  label = '从媒体库选择',
  multiple = false,
  disabled = false,
  onPick,
}: {
  tenantId: string;
  siteId: string;
  label?: string;
  multiple?: boolean;
  disabled?: boolean;
  onPick: (items: MediaItem[]) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="ghost" disabled={disabled} onClick={() => setOpen(true)}>
        {label}
      </Button>
      <MediaPickerDialog
        open={open}
        tenantId={tenantId}
        siteId={siteId}
        multiple={multiple}
        onClose={() => setOpen(false)}
        onConfirm={(items) => {
          setOpen(false);
          if (items.length > 0) onPick(items);
        }}
      />
    </>
  );
}
